import {
  ChangeDetectorRef,
  Component,
  inject,
  input,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  SecurityContext,
} from '@angular/core';
import { WebformService } from '@app/services/webform.service';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  MobiUiCheckboxValidators,
  MobiUiDatepickerValidators,
  MobiUiFileInputValidators,
  MobiUiPhoneInputValidators,
} from '@mobi/rwc-ui-components-ng-jslib';
import { getDate } from '@app/helpers/date';
import { replaceDateTokens, replaceRandomTokens } from '@app/helpers/tokens';
import { format } from 'date-fns';
// import { TealiumUtagService } from '@app/services/utag.service'
import { debounceTime, pairwise, skip, startWith, take } from 'rxjs/operators';
import { DOCUMENT, isPlatformServer } from '@angular/common';
import { Condition, Conditional } from '@app/models/form.model';
import { Subscription } from 'rxjs';
import { setTimeout$ } from '@app/helpers/timeout';
import { KeyValue } from '@app/services/webform.service';
import { applyXssValidation, sanitizePotentialXss } from './validators/xss';
import {
  deserializeConditional,
  evaluateWebformConfirmatioTemplate,
  flattenRawData,
  replaceFieldPlaceholders,
} from '@app/helpers/webform';
import { getVisitorId } from '@app/helpers/misc';
import { DomSanitizer } from '@angular/platform-browser';
import { TealiumUtagService } from '@app/services/utag.service';
import { DrupalService } from '@app/services/drupal.service';
import { FastlyChallengeService } from '@app/services/fastly-challenge.service';
import { Node } from '@app/models/entity.model';
import { PlatformContextService } from '@app/services/platform-context.service';
import { GaService } from '@app/services/ga.service';
import { FormAutofillService } from '@app/services/form-autofill.service';
import {
  AUTOFILL_URL_PARAM,
  AUTOFILL_URL_PARAM_ALIASES,
} from '@app/helpers/profile-prefill';

export type FormGroupEntry = any; // { key: string, default: any, validators: any[] }

const CONDITIONAL_STATE_SETTLE_DELAY_MS = 40;

@Component({
  standalone: false,
  selector: 'mobi-webform',
  templateUrl: './webform.component.html',
  styleUrls: ['./webform.component.scss'],
  // per-form autofill service: a fresh instance per rendered form so the banner's
  // "filled" state resets per form (never auto-import across forms)
  providers: [FormAutofillService],
  // Use on push strategy because the change detection needs to
  // be triggered at specific times (conditional field dependencies)
  // this also improves the performance as the change detection
  // will be only run when needed
})
export class WebformComponent implements OnInit, OnDestroy {
  @Input() webformId!: string;
  @Input() isParagraph: boolean = false;
  readonly params = input<URLSearchParams>(new URLSearchParams());

  currentPage: number = 0;
  webformData: KeyValue = {};
  webformSettings: KeyValue = {};
  webformKeys: string[] = [];
  hasError: boolean = false;
  formGroup!: UntypedFormGroup;
  messages: any[] = [];
  isLoading: boolean = true;
  isInitializing: boolean = true;
  submitting: boolean = false;
  isConfirmationPage: boolean = false;
  hasSteps: boolean = false;
  pageKeys: string[] = [];
  pages: KeyValue[] = [];
  formGroupData: KeyValue = {};
  fieldGroupMap: KeyValue = {};
  render: boolean = false;
  optionsElements: KeyValue = {};
  confirmationSettings: KeyValue = {};
  flattenedFields: KeyValue = {};
  #formData: KeyValue = {};
  #sumFields: KeyValue = {};
  #containerTypes = ['container', 'webform_flexbox', 'details'];
  #email: any = false;
  #states: { [key: string]: Conditional[] } = {};
  #removedControls: KeyValue = {};
  #removedContainerChilds: KeyValue = {};
  #calcResult: KeyValue = {};
  #formSub!: Subscription;
  #submittingSub!: Subscription;
  #currentPageSub!: Subscription;
  #formDataSub!: Subscription;
  #calcSub!: Subscription;
  #textfields = ['textfield', 'textarea', 'email'];
  #modifyTrackTimers: Record<string, ReturnType<typeof setTimeout>> = {};
  #autofillAttachTimer?: ReturnType<typeof setTimeout>;
  #lastTrackedFieldValues: Record<string, string> = {};

  readonly #tealium = inject(TealiumUtagService);
  readonly #formBuilder = inject(UntypedFormBuilder);
  readonly #cdr = inject(ChangeDetectorRef);
  readonly #route = inject(DrupalService);
  readonly #webform = inject(WebformService);
  readonly #sanitizer = inject(DomSanitizer);
  readonly #ga = inject(GaService);
  readonly #platformContext = inject(PlatformContextService);
  readonly #doc = inject(DOCUMENT);
  readonly #platformId = inject<Object>(PLATFORM_ID);
  readonly #challenge = inject(FastlyChallengeService);
  readonly #autofill = inject(FormAutofillService);

  constructor() {
    this.initSubscriptions();
  }

  initSubscriptions() {
    this.#submittingSub = this.#webform.submitting.subscribe((v: boolean) => {
      this.submitting = v;
      this.#cdr.markForCheck();
    });
    this.#formDataSub = this.#webform.formData.subscribe(
      (data: KeyValue) => (this.#formData = data)
    );
    this.#calcSub = this.#webform.calculatorResult.subscribe(
      (v: KeyValue) => (this.#calcResult = v)
    );
  }

  async ngOnInit(): Promise<void> {
    // Only render webforms on client
    if (isPlatformServer(this.#platformId)) return;

    // Bot protection: if this user lacks the Fastly challenge cookie, send them through a
    // challenge (real navigation) before rendering the form. Skip rendering if we redirect.
    if (await this.#challenge.maybeRedirectOnLoad()) return;

    this.render = true;
    this.isLoading = true;
    this.isConfirmationPage = false;
    this.#autofill.configureUrlAutofill(this.getAutofillUrlTrigger());

    try {
      this.webformData = await this.#webform.getWebformData(
        this.webformId,
        this.getQueryParams()
      );
      this.webformSettings = await this.#webform.getWebformSettings(this.webformId);

      this.#webform.formId = this.webformId;
      this.#webform.shouldPersist = !!this.webformSettings?.persist_form_state || false;

      // Get webform data
      this.webformKeys = Object.keys(this.webformData || {});
      // Process webform data to pass it into the @angular/form schema
      this.webformKeys.forEach((key: string) => {
        const field = this.webformData[key];

        if (field['#type'] === 'webform_wizard_page') {
          const pageData: KeyValue = {};
          const subfields: string[] = this.getContainerSubfields(field);
          subfields.forEach((subfield: string) => {
            const processed: FormGroupEntry[] = this.processField(field[subfield]);
            processed.forEach((entry: FormGroupEntry) => {
              pageData[entry.key] = [
                {
                  value: entry.default || '',
                  disabled: entry.disabled || false,
                },
                entry.validators,
              ];
            });
          });
          this.pages.push(field);
          this.pageKeys.push(field['#webform_key']);
          this.formGroupData[field['#webform_key']] = this.#formBuilder.group(pageData);
        } else {
          const processed: FormGroupEntry[] = this.processField(field);

          processed.forEach((entry: FormGroupEntry) => {
            this.formGroupData[entry.key] = [
              {
                value: entry.default || '',
                disabled: entry.disabled || false,
              },
              entry.validators,
            ];
          });
        }
      });

      this.hasSteps = this.pageKeys?.length > 0;
      if (this.#route.route()?.entity?.type === 'webform') {
        if (this.hasSteps) {
          this.#currentPageSub = this.#webform.currentPage
            .pipe(skip(1))
            .subscribe((v: number) => {
              this.currentPage = v;
              this.#cdr.markForCheck();

              this.trackFormDisplay();

              setTimeout(() => window.scrollTo(0, 0), 250);
            });
        } else {
          this.trackFormDisplay();
        }

        document?.addEventListener('webform:track:submit:incomplete', () => {
          this.trackSubmit(false);
        });
        document?.addEventListener('webform:track:form_display', () => {
          let errors: any = this.getFormGroupErrors(this.formGroup);
          if (this.hasSteps) {
            errors = this.getFormGroupErrors(
              this.formGroup.get(this.pageKeys[this.currentPage])
            );
          }
          this.#tealium.link({
            tealium_event: 'form_display',
            form_error: JSON.stringify(errors),
            ...this.utagData(),
          });
        });
      }

      this.formGroup = this.#formBuilder.group(this.formGroupData);
      this.generateFieldGroupMap();
      this.#formSub = this.formGroup.valueChanges
        .pipe(debounceTime(100), pairwise())
        .subscribe(([prev, next]: [any, any]) => this.handleValueChange(prev, next));
      this.handleInitialData();
      this.formGroup.updateValueAndValidity({ emitEvent: false });
      this.#webform.setPages(this.pages);

      // render the form but keep it hidden
      this.isLoading = false;

      // Detect changes as soon as the form is builded and
      // as soon as all the fields are in the form group
      // to allow correct conditional processing
      this.#cdr.detectChanges();

      const formValues: KeyValue = flattenRawData(
        this.formGroup.getRawValue(),
        this.pageKeys
      );
      this.#webform.setFormData(formValues);

      for (const state in this.#states) {
        this.runConditionals(state, this.#states[state]);
      }

      // display the form
      this.isInitializing = false;

      // Conditional visibility clears/disables controls on delayed callbacks. Attach only
      // after those callbacks settle so prefilled values survive for initially hidden fields.
      this.#autofillAttachTimer = setTimeout(() => {
        this.#autofill.attach({
          webformId: this.webformId,
          getControl: (id) => this.getAutofillControl(id),
          getField: (id) => this.flattenedFields[id],
          addMessage: (m) => this.messages.push(m),
          detectChanges: () => this.#cdr.detectChanges(),
        });
      }, CONDITIONAL_STATE_SETTLE_DELAY_MS);

      // trigger change detection after conditionals are run
      // and after everything is set up
      this.#cdr.detectChanges();
    } catch (e: any) {
      this.hasError = true;
      throw e;
    }
  }

  ngOnDestroy() {
    if (this.#formSub) this.#formSub.unsubscribe();
    if (this.#submittingSub) this.#submittingSub.unsubscribe();
    if (this.#currentPageSub) this.#currentPageSub.unsubscribe();
    if (this.#formDataSub) this.#formDataSub.unsubscribe();
    if (this.#calcSub) this.#calcSub.unsubscribe();
    Object.values(this.#modifyTrackTimers).forEach((timer) => clearTimeout(timer));
    this.#modifyTrackTimers = {};
    if (this.#autofillAttachTimer) clearTimeout(this.#autofillAttachTimer);
  }

  trackFormDisplay() {
    const data = this.utagData();
    this.#tealium.link({
      tealium_event: 'form_display',
      ...data,
    });

    if (this.webformSettings.mtracking_teal_event === 'calculator_page') {
      data.product_price =
        this.#calcResult?.annual_premium ||
        this.#calcResult?.annual_premium_professional ||
        0;
    }

    this.#tealium.view({
      tealium_event: this.webformSettings?.mtracking_teal_event || 'content_page',
      page_referrer: document.referrer,
      ...data,
    });
  }

  getFormGroupErrors(formGroup: any): any {
    const errors: any = {};

    Object.keys(formGroup.controls).forEach((key) => {
      const controlErrors = formGroup.get(key)?.errors;
      if (controlErrors) {
        errors[key] = controlErrors;
      }
    });

    return errors;
  }
  /**
   * Returns common utag link data for all form events
   *
   * @returns
   */
  utagData(): KeyValue {
    const { language } = (this.#route.route()?.entity as Node) || 'de';
    const customProperties: KeyValue = (this.webformSettings?.mtracking_add_values || '')
      .split('\r\n')
      .reduce((obj: KeyValue, str: string) => {
        const [key, value] = str.split('|');
        obj[key] = value;
        return obj;
      }, {});

    const pageUrlAlternate =
      `${this.#platformContext.getDomain('de') || window.location.origin}/form/${this.webformId}`.replaceAll(
        '_',
        '-'
      );

    const pageName =
      this.webformSettings?.mtracking_page_name ||
      document.querySelector('title')?.textContent ||
      '';

    const data: any = {
      page_referrer: this.#tealium.page_referrer,
      form_name: this.webformId,
      language_code: language || '',
      ...customProperties,
      page_name: pageName,
      page_url: window.location.pathname,
      page_url_alternate: pageUrlAlternate,
    };

    if (this.hasSteps) {
      data.form_step = `step-${this.currentPage + 1}`;
      data.page_url = `${data.page_url}/${data.form_step}`;
    }

    if (this.webformSettings?.mtracking_teal_event === 'calculator_page') {
      data.form_type = 'calculator';

      if (data.form_step) {
        data.calculator_step = data.form_step;
      }
    } else {
      data.form_type = 'drupal_webform';
    }

    return data;
  }
  /**
   * Requires the acceptance value of the compliance composite.
   */
  #complianceAcceptedValidator(control: AbstractControl): { required: true } | null {
    return [true, 1, '1'].includes(control.value?.accepted ?? control.value)
      ? null
      : { required: true };
  }

  /**
   * Custom validator for step validation on number inputs
   * @param {number} step The step value
   * @returns Validator function
   */
  #stepValidator(step: number) {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.value === null || control.value === '' || control.value === undefined) {
        return null;
      }
      const value = parseFloat(control.value);
      if (isNaN(value)) {
        return null;
      }
      const remainder = Math.abs((value * 1000000) % (step * 1000000)) / 1000000;
      const isValid = remainder < 0.000001 || Math.abs(remainder - step) < 0.000001;
      return isValid ? null : { step: { value: control.value, step: step } };
    };
  }

  /**
   * generate a map of the form pages to allow to detect the path of a field
   * inside of the angular forms structure. needed for conditionals
   */
  generateFieldGroupMap() {
    const rawValue: KeyValue = this.formGroup.getRawValue();
    Object.keys(rawValue).forEach((group: string) => {
      if (!this.pageKeys.includes(group)) return;
      Object.keys(rawValue[group]).forEach((field: string) => {
        this.fieldGroupMap[field] = group;
      });
    });
  }

  /**
   * Handle the loading of the initial and persisted data
   */
  handleInitialData() {
    if (this.#webform.shouldPersist) {
      this.#webform.loadPersistedData();
      // restructure flattened form data to be able to
      // use angular forms correctly with the pages
      const structuredData: KeyValue = {};
      Object.keys(this.#formData).forEach((field: string) => {
        const parent: string = this.fieldGroupMap[field] || '';
        if (parent) {
          if (!structuredData[parent]) structuredData[parent] = {};
          structuredData[parent][field] = sanitizePotentialXss(this.#formData[field]);
          return;
        }
        structuredData[field] = sanitizePotentialXss(this.#formData[field]);
        return;
      });
      this.formGroup.patchValue(structuredData || {});
    } else {
      const formValues: KeyValue = flattenRawData(
        this.formGroup.getRawValue(),
        this.pageKeys
      );
      this.#webform.setFormData(formValues);
    }
  }

  /**
   * Returns the angular forms control path of a specified field
   * to allow access to a nested control with formGroup.get()
   * @param field
   * @returns
   */
  getFieldPath(field: string): string {
    const parent: string = this.fieldGroupMap[field] || '';
    return parent ? `${parent}.${field}` : field;
  }

  /**
   * Handle value change of any field
   * in the form group
   * @param prev
   * @param next
   */
  handleValueChange(prev: any, next: any) {
    // get all values including disabled ones
    const formValues: KeyValue = flattenRawData(
      this.formGroup.getRawValue(),
      this.pageKeys
    );
    const keys: string[] = Object.keys(formValues);
    const flatPrev: KeyValue = flattenRawData(prev, this.pageKeys);
    const flatNext: KeyValue = flattenRawData(next, this.pageKeys);

    const prevJson = JSON.stringify(flatPrev);
    const nextJson = JSON.stringify(flatNext);
    if (prevJson === nextJson) return;

    this.#webform.setFormData(formValues);
    const sumFieldKeys = Object.keys(this.#sumFields);

    keys.forEach((key: string) => {
      const fieldPath = this.getFieldPath(key);
      const control: AbstractControl | null = this.formGroup.get(fieldPath);
      // Handle sum fields
      if (sumFieldKeys.includes(key)) {
        let sum: number = 0;
        this.#sumFields[key].forEach((field: string) => {
          // make sure ID is always correct
          const id = `${this.webformId}--${field.replace(`${this.webformId}--`, '')}`;
          if (formValues[id]) sum += parseInt(formValues[id]);
        });
        control?.setValue(sum.toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'"));
        control?.markAsTouched();
      }

      // abort change handling if field is disabled
      if (!flatPrev[key] && !flatNext[key]) return;

      if (flatPrev[key] !== flatNext[key] && !sumFieldKeys.includes(key)) {
        this.trackValueChange(
          key.replace(`${this.webformId}--`, ''),
          flatNext[key],
          control
        );
      }
    });

    for (const state in this.#states) {
      this.runConditionals(state, this.#states[state]);
    }
  }

  /**
   * Track value change of any field
   * in the form group
   * @param name
   * @param value
   */
  trackValueChange(name: string, value: any, control: AbstractControl | null) {
    if (this.submitting) return;

    // Process checkbox group data to have the correct format
    if (value instanceof Object) {
      const temp: any = [];
      Object.keys(value).forEach((k: string) => {
        if (value[k]) temp.push(k);
      });
      value = temp;
    }

    const normalizedFieldName = name.trim();
    const serializedValue = JSON.stringify(value);

    // Avoid duplicate form_modify events for unchanged values.
    if (this.#lastTrackedFieldValues[normalizedFieldName] === serializedValue) {
      return;
    }

    if (this.#modifyTrackTimers[normalizedFieldName]) {
      clearTimeout(this.#modifyTrackTimers[normalizedFieldName]);
    }

    // Delay emits so typing produces one event after user pause, not one per key.
    this.#modifyTrackTimers[normalizedFieldName] = setTimeout(() => {
      const data: KeyValue = {
        tealium_event: 'form_modify',
        form_field_name: normalizedFieldName,
        form_field_value: value,
        ...this.utagData(),
      };

      if (control?.errors) {
        data.form_error = Object.keys(control.errors)[0];
      }

      this.#tealium.link(data);
      this.#lastTrackedFieldValues[normalizedFieldName] = serializedValue;
      delete this.#modifyTrackTimers[normalizedFieldName];
    }, 800);
  }

  /**
   * Return all effective values of a form/step
   *
   * @param complete if the form is complete (final submit)
   * @returns
   */
  getFieldValuesForSubmit(complete: boolean): Object {
    const data = this.getFormData();
    if (this.hasSteps && !complete) {
      const formGroup: any = this.formGroup.get(this.pageKeys[this.currentPage]);
      const dataKeys = Object.keys(data);
      const stepData: any = {};
      for (const key in formGroup.controls) {
        const formatted = key.replace(`${this.webformId}--`, '');
        if (dataKeys.includes(formatted)) {
          stepData[formatted] = data[formatted];
        }
      }
    }

    for (const key in data) {
      if (
        data[key] === '' ||
        data[key] === 'null' ||
        key.startsWith('_') ||
        key.startsWith('pdf_') ||
        key === 'webform_id'
      ) {
        delete data[key];
      }

      if (data[key] instanceof Object) {
        data[key] = JSON.stringify(data[key]);
      }
    }

    return sanitizePotentialXss(data) as Object;
  }

  /**
   * Handle tracking of a form submission
   */
  trackSubmit(complete: boolean = false) {
    let errors: any = this.getFormGroupErrors(this.formGroup);
    if (this.hasSteps) {
      errors = this.getFormGroupErrors(
        this.formGroup.get(this.pageKeys[this.currentPage])
      );
    }

    if (Object.keys(errors || {}).length > 0) {
      return;
    }

    let data: any = {
      tealium_event: 'form_submit',
      form_complete: complete,
      ...this.utagData(),
    };

    data = {
      ...data,
      ...this.getFieldValuesForSubmit(complete),
    };

    if (this.#email) data.form_data_email = this.#formData[this.#email];
    this.#tealium.link(data);
  }

  /**
   * Handle tracking of a form confirmation
   */
  trackConfirmation() {
    setTimeout$(() => {
      const data: KeyValue = {
        ...this.utagData(),
      };
      if (this.webformSettings.mtracking_teal_event === 'calculator_page') {
        data.tealium_event = 'offer_request';
        data.agency_name = this.#ga.currentGa()?.addressName || '';
        data.product_price =
          this.#calcResult?.annual_premium ||
          this.#calcResult?.annual_premium_professional ||
          0;

        if (this.#webform.submissionType === 'conclude') {
          data.tealium_event = 'policy_conclusion';

          if (this.#calcResult?.annual_premium) {
            data.transaction_revenue_total = parseFloat(
              this.#calcResult.annual_premium
            ).toFixed(2);
            data.transaction_revenue_net = parseFloat(this.#calcResult.premium).toFixed(
              2
            );
          }

          if (this.#calcResult?.annual_premium_professional) {
            data.transaction_revenue_total = parseFloat(
              this.#calcResult.annual_premium_professional
            ).toFixed(2);
            data.transaction_revenue_net = (
              parseFloat(this.#calcResult.final_premium_rates_professional) +
              parseFloat(this.#calcResult.annual_premium_professional)
            ).toFixed(2);
          }

          data.contract_start_date =
            this.#formData[`${this.webformId}--ins_start_date`] ||
            format(new Date(), 'dd.MM.yyyy');
          data.contract_end_date = this.#calcResult.end_date;
        }
        if (this.#email) data.customer_email = this.#formData[this.#email];
      } else {
        data.tealium_event = 'form_success';
        if (this.#email) data.form_data_email = this.#formData[this.#email];
      }

      this.#tealium.link(data);
    }, 750);
  }

  /**
   * Returns the prepopulate parameter of a given field
   *
   * @param id
   */
  getQueryParams(): URLSearchParams {
    const params = this.params();

    // Paragraph and embedded forms are not route components, so they have no
    // explicit params input. Use the centralized route state in that case.
    return params.size > 0 ? params : this.#route.queryParams();
  }

  getPrepopulateParam(id: string): any {
    const cleanId = id.replace(`${this.webformId}--`, '');
    return this.getQueryParams().get(cleanId);
  }

  /** External callers can request profile prefill with `autofill`, `auth` or `prefill`. */
  getAutofillUrlTrigger(): boolean {
    return [AUTOFILL_URL_PARAM, ...AUTOFILL_URL_PARAM_ALIASES].some((key) =>
      this.getUrlFlag(key)
    );
  }

  getUrlFlag(key: string): boolean {
    const param = this.getQueryParams().get(key);
    return param === '1' || param?.toLocaleLowerCase() === 'true';
  }

  /**
   * Process a field in order to be able to use it with @angular/forms
   * @param field
   */
  processField(field: KeyValue): FormGroupEntry[] {
    this.flattenedFields[field['#webform_id']] = field;
    let fieldData: FormGroupEntry[] = [];

    // Skip the pdf generation field
    if (field['#type'] === 'mobi_webform_primcalc_pdfgenerate') return [];

    // Skip the login banner element (display only, no form control)
    if (field['#type'] === 'mobi_webform_login_banner') return [];

    if (field['#states']) {
      this.#states[field['#webform_id']] = deserializeConditional(field['#states']);
    }

    // Illustration sections only display editor content.
    if (field['#type'] === 'mobi_webform_illustration_section') return [];

    if (this.#containerTypes.includes(field['#type'])) {
      // Recursive process the container subfields
      const subfields: string[] = this.getContainerSubfields(field);
      subfields.forEach((subfield: string) => {
        const processed: FormGroupEntry[] = this.processField(field[subfield]);
        fieldData = [...fieldData, ...processed];
      });
    } else {
      fieldData = [];
      // Push the data model to the field data
      const validators: any[] = [];

      field['#default_value'] = field['#value'] || field['#default_value'] || null;

      if (field['#type'] === 'date' && field['#default_value']) {
        field['#default_value'] = format(getDate(field['#default_value']), 'dd.MM.yyyy');
      }

      // Process checkboxes default to match MobiUi data structure
      if (field['#type'] === 'checkboxes') {
        const defaults: { [key: string]: boolean } = {};
        if (Array.isArray(field['#default_value'])) {
          field['#default_value']?.forEach((entry: string) => {
            defaults[entry] = true;
          });
        }
        field['#default_value'] = defaults;
        // Add second option if only one present to
        // prevent getting only a boolean as a
        // field value -> MobiUi arch
        if (field['#options'].length === 1) {
          field['#options'].push({
            key: 'hidden',
            value: 'hidden',
            disabled: false,
          });
        }
      }

      if (field['#options']) {
        if (field['#type'] === 'select' && Array.isArray(field['#options'])) {
          field['#options'] = field['#options'].map((option: KeyValue) => ({
            ...option,
            key: String(option.key),
          }));
        }
        this.optionsElements[field['#webform_id']] = field['#options'];
      }

      // If it is a sum input, save sum informations and set default to 0
      if (field['#is_sum']) {
        this.#sumFields[field['#webform_id']] = field['#sum_fields']?.split('|');
        field['#default_value'] = '0';
      }

      if (field['#required'] && field['#type'] !== 'webform_address') {
        if (this.#textfields.includes(field['#type'])) {
          validators.push(Validators.required);
        } else {
          validators.push(Validators.required);
        }
      }
      if (field['#required'] && field['#type'] === 'checkbox') {
        validators.push(Validators.requiredTrue);
      }
      if (field['#type'] === 'mobi_webform_compliance_guidelines') {
        validators.push(this.#complianceAcceptedValidator);
      }
      if (field['#required'] && field['#type'] === 'checkboxes') {
        validators.push(MobiUiCheckboxValidators.atLeastOneSelected);
      }
      if (field['#pattern']) validators.push(Validators.pattern(field['#pattern']));
      if (field['#max'] && field['#type'] !== 'range') {
        validators.push(Validators.max(parseInt(field['#max'])));
      }
      if (field['#min'] && field['#type'] !== 'range') {
        validators.push(Validators.min(parseInt(field['#min'])));
      }
      if (field['#step'] && field['#type'] === 'number') {
        validators.push(this.#stepValidator(parseFloat(field['#step'])));
      }
      if (field['#minlength']) validators.push(Validators.minLength(field['#minlength']));
      if (field['#maxlength']) validators.push(Validators.maxLength(field['#maxlength']));
      if (field['#date_date_min']) {
        validators.push(
          MobiUiDatepickerValidators.minDate(getDate(field['#date_date_min']))
        );
      }
      if (field['#date_date_max']) {
        validators.push(
          MobiUiDatepickerValidators.maxDate(getDate(field['#date_date_max']))
        );
      }
      if (field['#type'] === 'email') validators.push(Validators.email);
      if (field['#type'] === 'tel') {
        validators.push(MobiUiPhoneInputValidators.validPhoneNumber);
      }

      if (field['#type'] === 'email') this.#email = field['#webform_id'];
      if (field['#type'] === 'tealium_visitor_id') {
        field['#default_value'] = getVisitorId(this.#platformId);
      }

      // Make sure select value is string as the default
      // value wont work with MobiUi component with a number
      if (
        field['#type'] === 'select' &&
        field['#default_value'] !== null &&
        typeof field['#default_value'] !== 'undefined'
      ) {
        field['#default_value'] = `${field['#default_value']}`;
      }
      if (field['#type'] === 'value') {
        field['#default_value'] = `${field['#default_value']}`;
      }

      if (
        field['#default_value'] &&
        field['#type'] !== 'checkbox' &&
        field['#type'] !== 'checkboxes' &&
        field['#type'] !== 'radios'
      ) {
        field['#default_value'] = replaceDateTokens(field['#default_value']);
        field['#default_value'] = replaceRandomTokens(field['#default_value']);
      }

      if (field['#prepopulate'] || this.webformSettings.prepopulate) {
        const value = this.getPrepopulateParam(field['#webform_id']);
        if (value && field['#type'] !== 'checkbox' && field['#type'] !== 'checkboxes') {
          if (field['#type'] !== 'number') field['#default_value'] = `${value}`;
        }
        if (value && field['#type'] === 'checkbox') {
          field['#default_value'] = value === 'true';
        }
        if (value && field['#type'] === 'checkboxes') {
          const defaults: { [key: string]: boolean } = {};
          if (Array.isArray(value)) {
            value?.forEach((entry: string) => {
              defaults[entry] = true;
            });
          } else {
            defaults[value] = true;
          }
          field['#default_value'] = defaults;
        }
      }

      if (field['#type'] === 'managed_file') {
        // Filesize validation
        if (field['#max_filesize']) {
          // Convert max_filesize from MB to bytes (1MB = 1,048,576 bytes)
          const maxSizeInBytes = field['#max_filesize'] * 1024 * 1024;
          validators.push(MobiUiFileInputValidators.maxFileSize(maxSizeInBytes));
        }

        // File type validation
        if (field['#file_extensions']) {
          const fileExtensions = field['#file_extensions'].split(' ');
          validators.push(MobiUiFileInputValidators.allowedFileTypes(fileExtensions));

          field['#allowed_files'] = field['#file_extensions']
            .split(' ')
            .map((ext: string) => `.${ext}`)
            .join(', ');
        } else {
          field['#allowed_files'] = '*';
        }

        // File count validation
        if (field['#multiple'] && typeof field['#multiple'] === 'number') {
          validators.push(MobiUiFileInputValidators.maxFilesCount(field['#multiple']));
        }
      }

      const defaultValue = field['#default_value'];
      // 'select' is excluded because its default value is intentionally stringified
      // above to match the stringified option keys used by the MobiUi component
      const excluded: string[] = [
        'value',
        'tel',
        'date',
        'hidden',
        'managed_file',
        'select',
      ];

      if (
        !field['#is_sum'] &&
        !excluded.includes(field['#type']) &&
        typeof defaultValue === 'string' &&
        !isNaN(Number(defaultValue)) &&
        !isNaN(parseInt(defaultValue))
      ) {
        field['#default_value'] = parseInt(defaultValue);
      }

      if (field['#type'] === 'range') {
        if (field['#is_rating']) {
          field['#default_value'] = null;
        } else {
          field['#default_value'] = field['#default_value'] || field['#min'] || '0';
        }
        field['#min'] = field['#min'] || 0;
        field['#max'] = field['#max'] || 100;
      }

      if (field['#type'] === 'mobi_webform_compliance_guidelines') {
        const value = field['#default_value'];
        field['#default_value'] = {
          accepted: [true, 1, '1'].includes(value?.accepted ?? value),
          contribute: [true, 1, '1'].includes(value?.contribute),
        };
      }

      fieldData.push({
        key: field['#webform_id'],
        default:
          field['#type'] === 'managed_file' && field['#multiple']
            ? field['#default_value'] || []
            : field['#default_value'] || null,
        // prevent sum field of being disabled because the validation wont work if disabled
        // the sum field is per default readonly and not editable
        disabled: field['#is_sum']
          ? false
          : field['#readonly'] || field['#disabled'] || false,
        validators,
      });
    }
    return fieldData;
  }

  scrollErrorIntoView() {
    this.formGroup.setErrors(this.formGroup.errors);
    document?.dispatchEvent(new CustomEvent('webform:track:form_display'));
    // highlight first invalid field
    const invalidFields = this.#doc
      .querySelector('form')
      ?.querySelectorAll(
        '.ng-invalid:not(.webform-field--processed_text):not(.webform-field--webform_markup):not(.webform-page):not(.webform-container):not(.webform-flexbox)'
      );

    if (!invalidFields) return;
    for (const field of invalidFields) {
      if (field.closest('*[hidden="true"]')) continue;
      field.scrollIntoView({ block: 'center', behavior: 'smooth' });
      break;
    }
  }

  /**
   * Submit form
   */
  async submit() {
    this.#cdr.detectChanges();
    this.formGroup.markAllAsTouched();
    this.formGroup.updateValueAndValidity();
    const hasXssAttempt = applyXssValidation(this.formGroup);

    // Make sure only one submission is going on
    let isSubmitting = undefined;
    const sub = this.#webform.submitting
      .pipe(take(1))
      .subscribe((value) => (isSubmitting = value));
    sub.unsubscribe();
    if (isSubmitting) return;
    if (
      hasXssAttempt ||
      this.formGroup.invalid ||
      this.formGroup.status === 'INVALID' ||
      this.formGroup.status === 'PENDING'
    ) {
      // highlight first invalid field
      this.scrollErrorIntoView();
    } else {
      try {
        const rawFormData = this.getFormData();
        const hasFiles = this.hasFileUploads();

        this.confirmationSettings = await this.#webform.submitWebformData(
          rawFormData,
          hasFiles
        );

        setTimeout$(() => this.handleConfirmation(), 250);
        this.trackSubmit(true);
      } catch (e: any) {
        console.error(e);

        let message = '';

        if (e.error?.error && Object.keys(e.error.error)) {
          Object.keys(e.error.error).forEach((key) => {
            message += `${e.error.error[key]} <br>`;
          });
        } else {
          message =
            e.error?.message ||
            e.message ||
            `An unknown error occurred: ${JSON.stringify(e)}`;
        }

        this.#tealium.link({
          tealium_event: 'system_event',
          system_type: 'error',
          message: message || JSON.stringify(e),
          ...this.utagData(),
        });

        this.messages.push({
          id: `error-${new Date().getTime()}`,
          level: 'warning',
          text: message,
        });

        this.#webform.logFormError(
          new Date().toISOString(),
          message || JSON.stringify(e),
          this.webformId
        );

        // detect changes on submit
        this.#cdr.detectChanges();
      }
    }
    this.#cdr.detectChanges();
  }

  /**
   * Handles the steps after the submission aka the confirmation
   */
  handleConfirmation() {
    // Replace all placeholder tokens with the correct formdata and parse the templates
    Object.keys(this.confirmationSettings).forEach((key: string) => {
      const value: number | boolean | string = this.confirmationSettings[key];
      if (typeof value !== 'string' || key === 'pdf_blob') return;
      let processed = replaceFieldPlaceholders(
        value,
        this.#formData,
        this.optionsElements,
        this.webformId
      );
      processed = evaluateWebformConfirmatioTemplate(processed);
      this.confirmationSettings[key] = sanitizePotentialXss(processed);
    });

    // Replace all placeholder tokens with the correct formdata and parse the templates
    this.confirmationSettings.confirmation_steps?.forEach(
      (step: KeyValue, index: number) => {
        const icon = replaceFieldPlaceholders(
          step.icon,
          this.#formData,
          this.optionsElements,
          this.webformId
        );
        this.confirmationSettings.confirmation_steps[index].icon = sanitizePotentialXss(
          evaluateWebformConfirmatioTemplate(icon)
        );
        const title = replaceFieldPlaceholders(
          step.title,
          this.#formData,
          this.optionsElements,
          this.webformId
        );
        this.confirmationSettings.confirmation_steps[index].title = sanitizePotentialXss(
          evaluateWebformConfirmatioTemplate(title)
        );
      }
    );

    switch (this.webformSettings.confirmation_type) {
      case 'page':
        window.scroll(0, 0);
        this.trackConfirmation();
        this.isConfirmationPage = true;
        this.#webform.setSubmitting(false);
        break;
      case 'redirect': {
        this.trackConfirmation();
        this.#webform.resetPersistedData();
        const url: string =
          this.confirmationSettings[`confirmation_redirect_${this.#route.language()}`] ||
          window.location.href;

        if (url.startsWith('http')) {
          window.location.href = url;
        } else {
          window.location.href = `${window.location.origin}${url}`;
        }
        break;
      }
      default:
        window.scroll(0, 0);
        this.#webform.resetPersistedData();
        this.#webform.setSubmitting(false);
        this.addInfo(
          `${this.confirmationSettings.confirmation_title}: ${this.confirmationSettings.confirmation_message}`
        );
        this.trackConfirmation();
        this.confirmationSettings = {};
        this.pages = [];
        this.ngOnDestroy();
        this.initSubscriptions();
        this.ngOnInit();
        break;
    }
  }

  /**
   * Add an info message
   * @param msg
   */
  addInfo(msg: string) {
    this.messages.push({
      id: `info-${new Date().getTime()}`,
      level: 'info',
      text: msg,
    });
  }

  /**
   * Remove a given message
   * @param messageForRemoval
   */
  removeMessage(messageForRemoval: any) {
    this.messages = this.messages.filter((msg) => msg.id !== messageForRemoval.id);
  }

  /**
   * Returns the form data with the correct
   * format for form submission
   */
  getFormData(): any {
    const data: { [key: string]: any } = { webform_id: this.webformId };
    const rawData: { [key: string]: any } = this.#formData;

    Object.keys(rawData).forEach((rawKey: string) => {
      const key: string = rawKey.replace(`${this.webformId}--`, '');

      // ignore generated fields (address for example)
      if (!key.includes('-generated_')) {
        let value: any = rawData[rawKey];

        if (
          this.flattenedFields[rawKey]?.['#type'] === 'mobi_webform_compliance_guidelines'
        ) {
          data[key] = {
            accepted: [true, 1, '1'].includes(value?.accepted ?? value) ? 1 : 0,
            contribute: [true, 1, '1'].includes(value?.contribute) ? 1 : 0,
          };
          return;
        }

        // Process checkbox group data to have the correct format
        if (
          value instanceof Object &&
          !Object.keys(value).includes('postal_code') &&
          !Object.keys(value).includes('address') &&
          !Array.isArray(value) // Don't process file arrays or other arrays
        ) {
          const temp: any = [];
          Object.keys(value).forEach((k: string) => {
            if (value[k]) temp.push(k);
          });
          value = temp;
        }

        data[key] = sanitizePotentialXss(value);
      }
    });

    return data;
  }

  /**
   * Check if the form contains file uploads (files from mobi-ui-file-input-major are always arrays)
   */
  hasFileUploads(): boolean {
    const rawData = this.#formData;
    return Object.keys(rawData).some((key) => {
      const value = rawData[key];
      return Array.isArray(value) && value.length > 0 && value[0] instanceof File;
    });
  }

  /**
   * Returns a given form control from all
   * possible storages
   * @param field
   */
  getControl(field: string): AbstractControl | string | null {
    if (this.formGroup.get(field)) return this.formGroup.get(field);
    if (this.#removedControls[field]) return this.#removedControls[field];
    if (this.#doc.querySelector(`*[data-form-container=${field}]`)) {
      return 'container-element';
    }
    return null;
  }

  /**
   * Returns a field control for profile autofill, including controls retained by
   * conditional visibility handling while their field is hidden.
   */
  getAutofillControl(id: string): AbstractControl | null {
    const control = this.formGroup.get(this.getFieldPath(id));
    if (control) return control;

    if (this.#removedControls[id]) return this.#removedControls[id];

    for (const children of Object.values(this.#removedContainerChilds)) {
      const child = (
        children as Array<{ id: string; control: AbstractControl | null }>
      ).find((entry) => entry.id === id);
      if (child?.control) return child.control;
    }

    return null;
  }

  /**
   * Handle adding a control to the form group and also handles
   * the pages with their subgroups
   *
   * @param id
   * @param control
   * @param container
   */
  addControl(id: string, control: any, container: string = '') {
    const fieldPath: string = this.getFieldPath(id);
    this.#webform.removeHiddenField(id);

    this.formGroup.get(fieldPath)?.enable();
    this.#autofill.prefillControl(id);

    this.#removedControls[id] = null;
    if (container) this.#removedContainerChilds[container][id] = null;
  }

  /**
   * Handle adding a control to the form group and also handles
   * the pages with their subgroups
   *
   * @param id
   * @param container
   */
  removeControl(id: string, container: string = '') {
    const fieldPath: string = this.getFieldPath(id);
    const clear =
      this.#doc
        .querySelector(`*[data-form-field="${id}"]`)
        ?.getAttribute('data-states-clear') === 'true';

    if (clear) {
      this.formGroup.get(fieldPath)?.setValue(null);
      this.formGroup.get(fieldPath)?.reset();
    }

    setTimeout(() => {
      this.#webform.addHiddenField(id);

      if (!container) {
        this.#removedControls[id] =
          this.formGroup.get(fieldPath) || this.#removedControls[id];
      }

      if (container) {
        this.#removedContainerChilds[container].push({
          id: id,
          control:
            this.formGroup.get(fieldPath) ||
            this.#removedContainerChilds[container][id]?.control ||
            null,
        });
      }

      this.formGroup.get(fieldPath)?.disable();
    }, 5);
  }

  /**
   * Run the conditionals that are bound
   * to a giben field
   * @param field
   * @param conditionals
   */
  runConditionals(field: string, conditionals: Conditional[]) {
    if (!conditionals.length) return;
    conditionals.forEach((conditional: Conditional) => {
      const results: boolean[] = this.evaluateConditional(conditional.conditions);
      if (!results.length) return;
      let result: boolean = false;

      // Evaluate results with the correct logical operator
      switch (conditional.operator) {
        case 'and':
          result = !!results.reduce((a: boolean, b: boolean) => a && b);
          break;
        case 'or':
          result = !!results.reduce((a: boolean, b: boolean) => a || b);
          break;
        case 'xor': {
          // convert boolesch array to numbers to allow bitwise xor
          const numbers: number[] = results.map((r: boolean) => +r);
          result = !!numbers.reduce((a: number, b: number) => a ^ b);
          break;
        }
      }

      const fieldPath = this.getFieldPath(field);
      const control: any = this.getControl(fieldPath);

      if (!control) return;

      // Run the results and apply the states
      switch (conditional.state) {
        case 'visible':
        case 'invisible': {
          if (conditional.state === 'invisible') result = !result;
          const elem = this.#doc.querySelector(`*[data-form-field="${field}"]`);
          // Form control logic only
          if (control !== 'container-element') {
            if (result && this.#removedControls[field]) {
              this.addControl(field, this.#removedControls[field]);
            } else if (!result) {
              this.removeControl(field, '');
            }
          }

          // Logic only for containers
          if (control === 'container-element') {
            if (result && this.#removedContainerChilds[field]) {
              // Add every persisted control that was in the container to the form group and reset the persisted state
              this.#removedContainerChilds[field].forEach(
                (child: { id: string; control: UntypedFormControl }) => {
                  if (child.control) {
                    this.addControl(child.id, child.control, field);
                  }
                }
              );
            } else if (!result) {
              // Get all form controls that are in the container and persist them. Then remove them from the form group
              this.#removedContainerChilds[field] = [];
              elem
                ?.querySelectorAll(`*[data-form-control]`)
                .forEach((control: Element) => {
                  const id: string = control.getAttribute('data-form-field') || '';
                  this.removeControl(id, field);
                });
            }
          }

          // Logic for containers and form controls
          if (result) {
            elem?.removeAttribute('hidden');
          } else if (control && !result) {
            elem?.setAttribute('hidden', 'true');
          }
          break;
        }
        case 'enabled':
        case 'disabled':
        case 'readonly':
          if (conditional.state === 'enabled') result = !result;
          setTimeout$(() => {
            if (result) {
              this.formGroup.get(fieldPath)?.disable();
            } else {
              this.formGroup.get(fieldPath)?.enable();
            }
          }, 15);
          break;
        case 'required':
        case 'optional':
          if (conditional.state === 'optional') result = !result;
          if (
            control !== 'container-element' &&
            !Object.keys(control?.value || {})?.includes('address_2')
          ) {
            if (result) {
              control.addValidators(Validators.required);
            } else {
              control.removeValidators(Validators.required);
            }
            control.updateValueAndValidity();
          }
          break;
      }
      this.#cdr.detectChanges();
    });
  }

  /**
   * Returns a page from the formgroup based on its key
   *
   * @param key
   * @returns
   */
  pageGroup(key: string): any {
    return this.formGroup.get(key);
  }

  /**
   * Evaluates a set of conditions and
   * returns the results
   *
   * @param conditions
   */
  evaluateConditional(conditions: Condition[]): boolean[] {
    const results: boolean[] = [];

    conditions.forEach((c: Condition) => {
      let value: any;
      if (c.field.includes('[')) {
        // Handle nested field strings
        const parts = c.field.split(/\[|\]/).filter((part) => part !== '');
        const data = this.getFormData()[parts[0]];
        // Handle checkboxes
        if (data?.length) {
          value = data.includes(parts[1]);
        } else {
          value = data;
        }

        if (typeof data === 'object' && data[parts[1]]) {
          value = data[parts[1]];
        }
      } else {
        // Handle simple field strings
        value = this.getFormData()[c.field];
      }

      if (value === false || value === null || typeof value === 'undefined') value = '';

      const evaluation = c.evaluation.replace('{value}', value);
      results.push(new Function(`"use strict"; return (${evaluation})`)());
    });

    return results;
  }

  /**
   * Get all the subfields of a container
   *
   * @param container
   */
  getContainerSubfields(container: KeyValue): string[] {
    return Object.keys(container)?.filter((k: string) => !k.startsWith('#'));
  }

  number(str: string): number {
    return parseInt(str);
  }

  sanitize(data: string) {
    return this.#sanitizer.sanitize(SecurityContext.HTML, data) || '';
  }
}
